/* ═══════════════════════════════════════════════════════════════════════════
   FORGE — describe a capability, get one built against the hardware present

   The interesting part of this is not that a model writes code. It is that the
   model is only allowed to write code about pins that exist and are bound. So
   the second step is always the same: here is what I grounded, here is what I
   could not, and the thing I could not ground is a missing sensor rather than
   a missing sentence. An ungrounded clause never reaches the code stage.

   Execute-class bindings do not dispatch on a model's say-so. The gate is the
   same one the pin binder uses, for the same reason: a probe is not consent.
   ══════════════════════════════════════════════════════════════════════════ */
import { useState, useMemo, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { html, M, F, Ico, Stream, Steps, FADE, Card, KV, Alert, Sec, go } from "./ui.js";
import { useJobModel } from "./models-api.js";
import { path, useAction, useApi, api } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { overrideSeat, seatIdOfPin, askBoard } from "./board-api.js";
import { toServerRole, mergeLiveSeatsOntoSoftPins } from "./board-seat-adapter.js";
import { PIN_ROLES, PINS } from "./edge-data.js";
import { resolveLiveForgeDraft, livePortIds, buildLiveClarifyQuestions, buildLiveComposePlan, formatLiveManifest, formatLiveComposePayload, buildLiveRuleView } from "./forge-live.js";

const STAGES = ["Describe","Clarify","Ground","Plan","Code","Sandbox","Dispatch"];

/* ── what the console can actually build ──────────────────────────────────
   Each recipe names the pins and ports it needs. Matching happens against the
   live pin table, so a recipe that needs a siren relay simply cannot be
   proposed on a node that has none.                                        */
const RECIPES = [
  /* The case that matters most is the one where the board cannot do it yet.
     "I want it to detect water" names no pin, no sensor and no consequence, and
     a model that answers it with code has guessed at all three. So this recipe
     answers with questions, and the questions are the ones whose answers change
     the wiring — not a form, and not a confirmation of what was already said. */
  {
    id:"water",
    match:/(water|wet|leak|flood|damp|moist|\u6f0f\u6c34|\u6c34)/i,
    title:"Water where it should not be",
    need:[], want:[{ role:"tamper", as:"a spare read-class seat" }],
    ask:[
      { id:"what", q:"Wet or not wet, or how deep?",
        why:"They are different parts. One is two wires and a digital seat; the other needs a bus seat and a calibration you will have to hold on to.",
        opts:[{ id:"leak",  t:"Wet or not wet", say:"A two-wire probe on one read-class pin. Nothing to calibrate." },
              { id:"level", t:"How deep it is", say:"An analogue or I\u00b2C part. Takes a bus seat and drifts, so it needs re-calibrating." }] },
      { id:"where", q:"Which seat should it take?",
        why:"There is no water sensor on this board. Something has to be wired, and I would rather you chose the pin than have me pick one and be wrong about what is already on it.",
        opts:[{ id:"free",  t:"An unassigned pin", say:"Twenty of the twenty-eight are free. I will take the lowest one and name it." },
              { id:"reuse", t:"Take over the tamper seat", say:"It is bound and verified. Reusing it means giving up tamper detection." }] },
      { id:"cost", q:"What happens when it is wet?",
        why:"This decides whether the capability is read-class or execute-class, and that decides whether it can ever dispatch without you.",
        opts:[{ id:"tell",  t:"Just tell someone", say:"Read-class. Builds and arms on its own." },
              { id:"cut",   t:"Cut power to something", say:"Execute-class. Drives a relay, so it will not dispatch without your confirmation.", risk:true }] },
    ],
    rule:{
      when:"the water probe reads closed",
      and:["the reading has held for 3 s, so a splash is not an event"],
      then:["raise a water belief at 0.55","notify the two responders on shift"],
    },
    say:"Now I can build it. You asked for wet-or-not on an unassigned pin, reporting only — so this is read-class and it arms itself. I have added a 3 second hold because a probe that fires on a splash will cry wolf until someone unbinds it. What I still cannot do is verify the pin: nothing is wired to it yet, so this builds disarmed and arms itself the first time the probe answers.",
    hi:["read-class","3 second hold","builds disarmed"],
    miss:[{ what:"a verified reading", why:"no probe is wired to the pin yet",
            fix:"Builds disarmed and arms itself on the first answer" }],
  },
  {
    id:"night_perimeter",
    match:/(night|dark|after|evening|perimeter|motion|pir|intrud)/i,
    title:"Night perimeter response",
    need:[{ role:"pir", as:"the trigger" }, { role:"siren", as:"the response" }],
    want:[{ port:"mipi_csi", as:"evidence" }],
    rule:{
      when:"GPIO4 · PIR zone west reads a rising edge",
      and:["local time is between 20:00 and 06:00","GPIO5 · door contact reads closed"],
      then:["hold a frame from Cam 04 as evidence",
            "raise an intrusion belief at 0.6 and let the gate decide",
            "sound GPIO15 · siren relay for 10 s if the gate approves"],
    },
    say:"Three of the four things you asked for are already on this board. The trigger is GPIO4, the response is the siren relay on GPIO15, and Cam 04 can hold the frame. What I cannot ground is «after dark» as a light level — this node has no ambient light sensor, so I have written it as a clock window instead. That is a weaker condition and you should know that it is.",
    hi:["GPIO4","GPIO15","Cam 04","clock window"],
    miss:[{ what:"«after dark» as a measured light level", why:"no ambient light sensor is bound on this node",
            fix:"Substituted a 20:00–06:00 clock window" }],
  },
  {
    id:"door_held",
    match:/(door|gate|held|ajar|open too long|strike)/i,
    title:"Door held open",
    need:[{ role:"door", as:"the sensor" }],
    want:[{ role:"siren", as:"the escalation" }],
    rule:{
      when:"GPIO5 · door contact reads open",
      and:["it has stayed open for longer than 90 s"],
      then:["raise a held-open belief at 0.5",
            "notify the two responders on shift",
            "escalate to GPIO15 · siren relay if it is still open at 180 s"],
    },
    say:"This one grounds cleanly. GPIO5 is the door contact and it is verified, so the dwell timer runs on the device rather than in the cloud — the rule still fires if the uplink is down. The escalation lands on the siren relay, which is execute-class, so it will need your confirmation before it dispatches.",
    hi:["GPIO5","GPIO15","execute-class"],
    miss:[],
  },
  {
    id:"thermal",
    match:/(temperature|heat|hot|cold|thermal|degrees|°c)/i,
    title:"Thermal excursion",
    need:[{ role:"temp", as:"the reading" }],
    want:[],
    rule:{
      when:"GPIO7 · DS18B20 reports above 38 °C",
      and:["the reading has held for 5 min","the sensor probed within the last 10 min"],
      then:["raise a thermal belief at 0.45","notify the site owner once per hour at most"],
    },
    say:"GPIO7 carries the DS18B20 and it is claimed but not yet verified — it has reported 31.4 °C and nothing has confirmed the probe. I will build this, but it stays disarmed until the pin verifies, because a threshold rule on an unverified sensor is a false-alarm generator.",
    hi:["GPIO7","claimed but not yet verified","stays disarmed"],
    miss:[{ what:"an armed state", why:"GPIO7 is claimed, not verified",
            fix:"Capability builds disarmed and arms itself when the pin verifies" }],
  },
  {
    id:"snapshot",
    match:/(snapshot|photo|frame|camera|picture|image|record)/i,
    title:"Evidence on trigger",
    need:[{ port:"mipi_csi", as:"the frame source" }],
    want:[{ role:"pir", as:"the trigger" }],
    rule:{
      when:"any read-class pin on this node changes state",
      and:["the node has held a frame less than once in the last 30 s"],
      then:["hold one frame from Cam 04","attach it to the event as evidence, unpublished"],
    },
    say:"Cam 04 is on the MIPI-CSI lane and it is live, so the frame source is real. I have rate-limited it to one frame per 30 s — the constraint is one camera per node, and a trigger storm would otherwise queue frames the encoder cannot clear.",
    hi:["MIPI-CSI","one frame per 30 s","one camera per node"],
    miss:[],
  },
];

/* Soft≠Live Page43 + Page98: Live compose bus · liquid residual · · Live CTAs. */

/* ═══════════════════════════════════════════════════════════════════════
   Soft ASK_DEFAULT brochure moved to forge-live.js POLICY_ASK_DEFAULT.
   Clarify questions are Live seat keep/drop + execute confirm first. */
export function Forge({ pins, setPins, ports, device, toast }){
  /* Live preference by purpose — Settings → Model access is the single write. */
  const designJob = useJobModel("design");
  const reviewJob = useJobModel("review");
  const design = designJob.provider;
  const review = reviewJob.provider;
  const { run, busy, error, envelope } = useAction();
  const [text,setText] = useState("");
  const [at,setAt]     = useState(0);
  const [rec,setRec]   = useState(null);
  const [ans,setAns]   = useState({});
  const [said,setSaid] = useState(false);
  const [conf,setConf] = useState(false);
  const [sent,setSent] = useState(false);
  const [composeOut,setComposeOut] = useState(null);
  const [sand,setSand] = useState("idle");   /* idle · attest · pass */
  const [attest,setAttest] = useState({});
  const [askLive,setAskLive] = useState(null); /* Live board/ask answer */
  const [askBusy,setAskBusy] = useState(false);
  const [probeBusy,setProbeBusy] = useState(false);

  const did = String((device && device.id) || "").trim();
  /* Soft≠Live upgrade: Live GET …/pins hydrates seats into Forge before compose. */
  const pinsLive = useApi(did ? path("get__devices_p_pins", { pathParams: [did] }) : "");
  const portsLive = useApi(did ? path("get__devices_p_ports", { pathParams: [did] }) : "");
  const liveSeatItems = useMemo(() => {
    const root = pinsLive.data || {};
    const items = root.items || root.pins || root.seats || [];
    return Array.isArray(items) ? items : [];
  }, [pinsLive.data]);
  const livePortSet = useMemo(() => livePortIds(portsLive.data), [portsLive.data]);

  useEffect(() => {
    if (!did || !setPins) return;
    if (!liveSeatItems.length && pinsLive.loading) return;
    setPins(mergeLiveSeatsOntoSoftPins(PINS.map((p) => ({ ...p })), liveSeatItems));
  }, [did, liveSeatItems, pinsLive.loading, setPins]);

  const bound = useMemo(()=> new Set(pins.filter(p=>p.role).map(p=>p.role)), [pins]);
  const liveBound = liveSeatItems.filter((s) => s && (s.role || s.assigned_role || s.state === "bound" || s.state === "verified" || s.state === "confirmed" || (s.binding && s.binding.role)));
  const liveConfirmed = liveSeatItems.filter((s) => {
    const st = String(s.state || s.status || (s.binding && s.binding.state) || "").toLowerCase();
    return st === "verified" || st === "confirmed" || s.confirmed === true;
  });

  /* Soft≠Live Page27: Soft recipe match is not the gate — Live seats/ports + Ask are. */
  const submit = async ()=>{
    const deviceId = String((device && device.id) || "").trim();
    setAskLive(null);
    let askOut = null;
    if (deviceId && String(text || "").trim().length >= 3) {
      setAskBusy(true);
      try {
        askOut = await askBoard(deviceId, { question: text });
        if (askOut && askOut.ok !== false) {
          setAskLive(askOut);
          toast("Live Board Ask answered", "go");
        } else {
          toast((askOut && askOut.error) || "Board Ask refused", "warn");
        }
      } catch (e) {
        toast(String((e && e.message) || e || "Board Ask failed"), "warn");
      } finally {
        setAskBusy(false);
      }
    } else if (!deviceId) {
      toast("Open a fleet device first — Board Ask needs a Live device id", "warn");
    }
    pinsLive.reload && pinsLive.reload();
    portsLive.reload && portsLive.reload();
    let seatsForDraft = liveSeatItems;
    let portsForDraft = livePortSet;
    if (deviceId) {
      try {
        const freshPins = await api(path("get__devices_p_pins", { pathParams: [deviceId] }), { method: "GET" });
        const items = (freshPins && (freshPins.items || freshPins.pins || freshPins.seats)) || [];
        if (Array.isArray(items)) {
          seatsForDraft = items;
          if (setPins) setPins(mergeLiveSeatsOntoSoftPins(PINS.map((p) => ({ ...p })), items));
        }
        const freshPorts = await api(path("get__devices_p_ports", { pathParams: [deviceId] }), { method: "GET" });
        portsForDraft = livePortIds(freshPorts);
      } catch (_e) { /* keep hydrated snapshot */ }
    }
    const draft = resolveLiveForgeDraft({
      text,
      recipes: RECIPES,
      liveSeatItems: seatsForDraft,
      livePortSet: portsForDraft,
      askPayload: askOut,
    });
    if (!draft) {
      setRec("none"); setAt(1); return;
    }
    setRec(draft); setSaid(false); setAns({}); setSand("idle"); setAttest({}); setAt(1);
    toast(
      draft.source === "live_partial"
        ? "Live partial · missing seats — Soft brochure is not truth"
        : ("Live-grounded draft · " + (draft.need || []).length + " seats"),
      draft.source === "live_partial" ? "warn" : "go",
    );
  };

  const qs      = rec && rec!=="none" ? buildLiveClarifyQuestions(rec) : [];
  const asked   = qs.length > 0 && qs.every(q=> ans[q.id]);
  /* A choice can raise the class of the whole capability. Answering "cut power
     to something" turns a reporting rule into one that drives a relay, and the
     gate has to follow the answer rather than the recipe it started from. */
  const risky   = qs.some(q=> (q.opts.find(o=>o.id===ans[q.id])||{}).risk);

  const EX = ["When motion trips after dark and the door is closed, sound the siren and keep a frame.",
              "Tell me if a door stays open longer than 90 seconds.",
              "Warn me if it goes above 38 degrees for five minutes."];

  const livePlan = useMemo(() => {
    if (!rec || rec === "none") return null;
    return buildLiveComposePlan({
      rec,
      ans,
      device,
      pins,
      seatIdOfPin,
      toServerRole,
    });
  }, [rec, ans, device, pins]);
  const liveRule = useMemo(
    () => (rec && rec !== "none" ? buildLiveRuleView(rec, ans, livePlan) : null),
    [rec, ans, livePlan],
  );
  const liveManifestText = livePlan ? formatLiveManifest(livePlan) : "";
  const livePayloadText = livePlan ? formatLiveComposePayload(livePlan) : "";
  const isExec = !!(livePlan && livePlan.class === "execute") || risky;

  const copyLivePayload = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(livePayloadText);
        toast("Live compose payload copied", "go");
      } else {
        toast("Clipboard unavailable - select the payload text", "warn");
      }
    } catch (e) {
      toast(String((e && e.message) || e || "Copy failed"), "warn");
    }
  };

  /* Soft≠Live: HITL checklist only — Soft dry-run fire counts / fixture rows deleted.
     Compose stays locked until every checklist item is human-confirmed. */
  const CHECKS = [
    ["Compiles for this target",       "built against the same toolchain the node runs"],
    ["Touches only bound pins",        "every pin it names is one this board actually has"],
    ["Cannot widen the node policy",   "refused a permission it was not already granted"],
    ["Terminates",                     "no path through it runs unbounded"],
    ["Will not drive until Compose",   "checklist unlocks Live forge/compose — no Soft replay invents fires"],
  ];
  const allAttested = CHECKS.every((_, i) => attest[i]);
  const beginAttest = ()=>{ setSand("attest"); setAt(5); };
  const confirmAttest = ()=>{
    if (!allAttested) {
      toast("Confirm every checklist item — the device has not verified anything", "warn");
      return;
    }
    setSand("pass");
    pinsLive.reload && pinsLive.reload();
    toast("Operator checklist recorded · Live compose unlocked (no Soft dry replay)", "warn");
  };
  const reProbeLive = async ()=>{
    if (!did) { toast("Bind a device first — Soft geometry alone is not Live", "warn"); return; }
    setProbeBusy(true);
    try {
      const out = await run(
        path("post__devices_p_commands", { pathParams: [did] }),
        { method: "POST", body: { cmd: "ping", params: {} } },
      );
      pinsLive.reload && pinsLive.reload();
      portsLive.reload && portsLive.reload();
      if (out) toast("Live re-probe · ping queued · seats/ports refreshed", "go");
      else toast(error || "Ping refused — seats still re-read from Live", "warn");
    } finally {
      setProbeBusy(false);
    }
  };
  const boundCount = pins.filter((p) => p.role).length;
  const livePortCount = livePortSet.size;
  const liveSeatCount = liveBound.length;
  const liveConfirmedCount = liveConfirmed.length;

  const dispatch = async ()=>{
    const did = String((device && device.id) || "").trim();
    if (!did) { toast("No device id — open a fleet device first", "bad"); return; }
    const plan = livePlan || buildLiveComposePlan({
      rec, ans, device, pins, seatIdOfPin, toServerRole,
    });
    const pin_bindings = (plan && plan.body && plan.body.pin_bindings) || [];
    if (!pin_bindings.length) {
      toast("No Live seats kept for compose — Keep at least one seat in Clarify", "warn");
      return;
    }
    const out = await run(path("post__devices_p_forge_compose", { pathParams: [did] }), {
      body: plan.body,
    });
    if (!out) {
      toast(/board_disabled|503/i.test(error || "")
        ? "Compose refused — board/registry flag off (not Soft-dispatched)"
        : /404|not found/i.test(error || "")
          ? "Compose route missing — Live forge not mounted (Soft invent deleted)"
        : "Forge compose refused · Live", "bad");
      return;
    }
    setComposeOut(out);
    setSent(true);
    setAt(6);
    toast(out.ok
      ? ("Compose applied · Live · " + (out.pins_applied || []).length + " pins · caps " + (out.capabilities ? "ok" : "skipped"))
      : ("Compose partial · Live · " + (out.pin_errors || []).length + " pin errors"), out.ok ? "go" : "warn");
  };

  return html`
    <div className="fg fg--liquid" data-cmc-forge="page43" data-cmc-page="page98-forge">
      <div className="fg__head">
        <span className="fg__badge"><${Ico} d="brain" w=${13}/>Capability forge · Live · Page98</span>
        <p>Describe the behaviour you want. Grounding is Live seats/ports only —
           Soft brochure recipes never ship as truth. Compose posts real pin bind/confirm.</p>
      </div>
      <${Alert} tone="info" title="Live compose bus armed"
        say="Plan/Code show the exact POST /devices/{id}/forge/compose body. Soft C fragment and Soft brochure manifest theatre stay deleted. Sandbox is HITL. Dispatch is the Live write. All primary HUD CTAs carry · Live."/>
      ${envelope
        ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
        : null}

      <${Steps} at=${at} items=${STAGES}/>

      <${Card} title="What should this node do?" meta=${"Live seats " + liveSeatCount + " · Soft geometry " + bound.size}>
        <textarea className="fg__in" rows=${3} value=${text} placeholder="Say it plainly…"
          onChange=${e=>{ setText(e.target.value); setRec(null); setAskLive(null); setAt(0); setSent(false); setConf(false); }}/>
        <div className="fg__ex">
          ${EX.map(x=> html`
            <button type="button" key=${x} className="fg__chip" onClick=${()=>{ setText(x); setRec(null); setAskLive(null); setAt(0); }}>${x}</button>`)}
        </div>
        <button type="button" className="btn btn--go" disabled=${askBusy || busy || !String(text||"").trim()}
          onClick=${submit} style=${{ marginTop:10 }}>
          <${Ico} d="spark" w=${14}/>${askBusy ? "Asking board · Live…" : "Ground against Live board · Live"}</button>
        <p className="fg__note">Live Board Ask + Live seats decide the draft. Soft recipe keywords never win alone. Nothing is written until Compose.</p>
        ${askLive && html`<${Sec} title="Live Board Ask" meta="POST …/board/ask">
          <pre className="note" style=${{ whiteSpace:"pre-wrap", fontFamily:"var(--mono)", fontSize:"11px", margin:0 }}>
${JSON.stringify((askLive.data && (askLive.data.answer || askLive.data)) || askLive.answer || askLive, null, 2).slice(0, 1200)}</pre>
          <p className="note">Live facts — draft below must still clear Live seat needs.</p>
        <//>`}
      </${Card}>

      <${AnimatePresence}>
        ${rec==="none" && html`
          <${M.div} key="none" initial=${{ opacity:0, y:8 }} animate=${{ opacity:1, y:0 }}
              exit=${{ opacity:0 }} transition=${FADE}>
            <${Card} title="Live could not ground that"
                meta="no capability written · Soft brochure refused">
              <${Alert} tone="warn" title="No Live-carried draft"
                say="Soft brochure recipes are not used as truth. Either Board Ask failed, or Live seats/ports do not carry what you described."/>
              <div className="grid g2" style=${{ marginBottom:10 }}>
                <${KV} k="Live seats (API)" v=${pinsLive.loading?"…":String(liveSeatCount)} tone=${liveSeatCount?"go":"warn"}/>
                <${KV} k="Live ports (API)" v=${portsLive.loading?"…":String(livePortSet.size)} tone=${livePortSet.size?"go":"warn"}/>
                <${KV} k="Board Ask" v=${askLive ? "on file" : "none"} tone=${askLive?"go":"warn"}/>
                <${KV} k="Soft geometry bound" v=${bound.size+"/"+PIN_ROLES.length}/>
              </div>
              ${askLive && html`
                <p className="note" style=${{ whiteSpace:"pre-wrap" }}>${String((askLive.data && askLive.data.answer) || askLive.answer || "").slice(0, 400)}</p>`}
              <div className="btn-row fg__acts" style=${{ marginTop:10, flexWrap:"wrap", gap:8 }}>
                <button className="btn btn--ai btn--sm" type="button"
                  disabled=${!did || probeBusy}
                  onClick=${()=>reProbeLive()}>
                  <${Ico} d="refresh" w=${13}/>${probeBusy ? "Probing · Live…" : "Re-probe · Live"}</button>
                <button className="btn btn--ghost btn--sm" type="button"
                  onClick=${()=>go("/fleet/device/"+encodeURIComponent(did))}>
                  Open Board · Live</button>
              </div>
            </${Card}>
          <//>`}

        ${rec && rec!=="none" && html`
          <${M.div} key=${rec.id} initial=${{ opacity:0, y:10 }} animate=${{ opacity:1, y:0 }}
              exit=${{ opacity:0 }} transition=${FADE}
              style=${{ display:"flex", flexDirection:"column", gap:14 }}>

            ${/* Before anything is grounded, the questions. A sentence like
                  "I want it to detect water" is not an instruction — it is the
                  beginning of one, and the gap between them is where a model
                  guesses. Every question below changes what gets built, which
                  is the only reason it is worth your time to answer. */""}
            <${Card} ai title="Clarify - Live seats first"
                meta=${(rec.source === "live" ? "Live draft · " : rec.source === "live_partial" ? "Live partial · " : "")
                  + Object.keys(ans).length + " of " + qs.length + " answered"}>
              <p className="sub">Keep / Drop and execute confirm bind to Live seat_ids. Soft policy questions
                only refine cost / offline after seats. Nothing invents a binding the Live board does not have.</p>
              <div className="ask">
                ${qs.map((q,i)=> html`
                  <div key=${q.id} className="ask__q" data-done=${ans[q.id]?"1":"0"} data-kind=${q.kind||"policy"}>
                    <span className="ask__n">${i+1}</span>
                    <div className="ask__b">
                      <div style=${{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:6 }}>
                        <b style=${{ margin:0 }}>${q.q}</b>
                        ${q.kind === "live_seat" && html`<span className="chip" data-tone="ai">Live seat</span>`}
                        ${q.kind === "live_exec" && html`<span className="chip" data-tone="warn">Execute</span>`}
                        ${q.kind === "policy" && html`<span className="chip" data-tone="off">Policy</span>`}
                      </div>
                      <em>${q.why}</em>
                      <div className="ask__o">
                        ${q.opts.map(o=> html`
                          <button key=${o.id} className="ask__c" data-sel=${ans[q.id]===o.id?"1":"0"}
                              data-risk=${o.risk?"1":"0"}
                              onClick=${()=>setAns(a=>({ ...a, [q.id]:o.id }))}>
                            <b>${o.t}</b><span>${o.say}</span>
                          </button>`)}
                      </div>
                      ${q.kind === "live_seat" && q.seat_id && html`
                        <button className="btn btn--ghost btn--sm" type="button" style=${{ marginTop:8 }}
                          disabled=${!did || askBusy}
                          onClick=${async ()=>{
                            setAskBusy(true);
                            try {
                              const out = await askBoard(did, { question: "What is bound on " + q.seat_id + "?", seat_id: q.seat_id });
                              if (out && out.ok !== false) {
                                setAskLive(out);
                                toast("Live re-ask · " + q.seat_id, "go");
                              } else toast((out && out.error) || "Board Ask refused", "warn");
                            } finally { setAskBusy(false); }
                          }}>
                          <${Ico} d="refresh" w=${13}/>Re-ask Live · ${q.seat_id}</button>`}
                    </div>
                  </div>`)}
              </div>
              ${!asked && html`
                <p className="note">Nothing has been written. Compose stays locked until every
                  Live seat and policy question above has an answer.</p>`}
            <//>

            ${asked && html`
            <${Card} title=${rec.title}
                meta=${(rec.source === "live" ? "Live-grounded · " : rec.source === "live_partial" ? "Live partial · " : "")
                  + "design " + design.label}>
              <div className="fg__say">
                <${Stream} text=${rec.say} hi=${rec.hi} speed=${11} onDone=${()=>{ setSaid(true); setAt(3); }}/>
              </div>

              ${said && html`
                <${F}>
                  <div className="fg__gr">
                    ${[...rec.need,...rec.want].filter((n)=>{
                      const key = String(n.seat_id || n.role || "").trim();
                      if (!key) return true;
                      return ans["seat_" + key] !== "drop";
                    }).map(n=>{
                      const p = n.role ? pins.find(x=>x.role===n.role) : null;
                      const pt = n.port ? ports.find(x=>x.id===n.port) : null;
                      const livePortOk = n.port ? livePortSet.has(n.port) : false;
                      const ok = n.seat_id
                        ? true
                        : (p ? true : !!(pt && (livePortOk || pt.state==="live")));
                      const label = n.seat_id || (p ? p.pin : (pt && pt.label)) || (n.role || n.port);
                      const detail = n.seat_id
                        ? ((n.state || "Live seat") + (n.as ? (" · " + n.as) : ""))
                        : (p ? p.fn : (pt && pt.bound)) || n.as || "—";
                      return html`
                        <div key=${(n.seat_id||n.role||n.port)} className="fg__gr__r" data-ok=${ok && !n.missing?"1":"0"}>
                          <i/><b>${label}</b>
                          <span>${detail}</span><em>${n.as || n.role || n.port}</em>
                        </div>`;
                    })}
                  </div>
                  ${rec.miss.length>0 && rec.miss.map(m=> {
                    const did = String((device && device.id) || "").trim();
                    const bound = m.role
                      ? pins.find((x) => x.role === m.role && seatIdOfPin(x))
                      : null;
                    const openBoard = () => {
                      if (!did) { toast("No device id for Board deep-link", "warn"); return; }
                      const free = (pins || []).find((x) => !x.role && seatIdOfPin(x));
                      /* Prefer Soft pin id for Board3D sel; seat_id also resolves in Estate. */
                      const focus = free
                        ? String(free.pin || seatIdOfPin(free) || "").trim()
                        : "";
                      const hash = "/fleet/device/" + encodeURIComponent(did)
                        + (focus ? ("?seat=" + encodeURIComponent(focus)) : "");
                      go(hash);
                      toast(
                        focus
                          ? ("Board · focus " + focus + " · Live bind (not Soft override)")
                          : "Board tab · bind/confirm the missing seat · Live pin API",
                        "warn",
                      );
                    };
                    const forceIsolate = async () => {
                      if (!did || !bound) {
                        toast("No bound seat for override — bind on Board first", "warn");
                        return;
                      }
                      const seat = seatIdOfPin(bound);
                      const out = await overrideSeat(did, seat, {
                        forced_state: "isolate",
                        ttl_seconds: 900,
                        operator_reason: "CMC Forge · isolate unbound miss " + String(m.what || seat),
                      });
                      if (!out.ok) { toast(out.error || "Override refused", "bad"); return; }
                      toast("Live pin override · isolate · " + seat, "warn");
                    };
                    return html`
                    <div key=${m.what} className="fg__miss">
                      <${Ico} d="alert" w=${14}/>
                      <span><b>Could not ground ${m.what}.</b> ${m.why}. ${m.fix}.</span>
                      <button className="fg__by" type="button" onClick=${openBoard}
                        title="Deep-link Fleet Board — Live bind/confirm (not Soft override toast)">
                        Bind on Board · Live</button>
                      ${bound ? html`
                        <button className="fg__by" type="button" onClick=${forceIsolate}
                          title="POST …/pins/{seat}/override · isolate">
                          Isolate seat · Live</button>` : null}
                    </div>`;
                  })}
                <//>`}
            </${Card}>`}

            ${said && html`
              <${F}>
                <${Card} title="Live plan - what compose will write"
                    meta=${(livePlan && livePlan.class) || "plan"}>
                  ${liveRule ? html`
                    <div className="fg__rule">
                      <div className="fg__rule__r"><u>when</u><span>${liveRule.when}</span></div>
                      ${liveRule.and.map(a=> html`
                        <div key=${a} className="fg__rule__r"><u>and</u><span>${a}</span></div>`)}
                      ${liveRule.then.map((t,i)=> html`
                        <div key=${t} className="fg__rule__r" data-then="1">
                          <u>${i===0?"then":""}</u><span>${t}</span></div>`)}
                    </div>
                    ${liveRule.softFoot ? html`
                      <p className="note" style=${{ marginTop:10 }}>Soft recipe footnote (not compose truth): ${liveRule.softFoot}</p>` : null}
                  ` : html`<p className="note">Answer Clarify first — Live plan needs Keep/Drop seats.</p>`}
                  ${isExec && html`
                    <div className="fg__gate">
                      <${Ico} d="lock" w=${14}/>
                      <span>Execute-class Live seats carry confirm flags. Soft dry-run theatre is deleted —
                        nothing drives until you attest and Compose.</span>
                    </div>`}
                </${Card}>

                <div className="grid g2">
                  <${Card} title="Live compose manifest" meta="from Clarify seats">
                    <pre className="fg__code">${liveManifestText || "(empty — keep at least one Live seat)"}</pre>
                  </${Card}>
                  <${Card} title="Live compose payload" meta="POST forge/compose body">
                    <pre className="fg__code">${livePayloadText || "(empty)"}</pre>
                    <div className="btn-row" style=${{ marginTop:10, flexWrap:"wrap", gap:8 }}>
                      <button className="btn btn--ai btn--sm" type="button"
                        disabled=${!livePayloadText}
                        onClick=${()=>copyLivePayload()}>
                        <${Ico} d="check" w=${13}/>Copy Live payload · Live</button>
                      <button className="btn btn--ghost btn--sm" type="button"
                        onClick=${()=>{ setAt(4); toast("Plan rebuilt from Clarify answers · Live", "go"); }}>
                        <${Ico} d="refresh" w=${13}/>Rebuild plan · Live</button>
                    </div>
                    <p className="note" style=${{ marginTop:8 }}>Soft C device-fragment theatre is deleted. This JSON is what Dispatch posts.</p>
                  </${Card}>
                </div>

                ${/* Nothing here is trusted because a model wrote it. It is
                      trusted because it was run somewhere it could not do harm
                      and it came back clean — and if it does not come back
                      clean, dispatch never opens. */""}
                <${Card} title="Sandbox" meta=${sand==="pass" ? "checklist attested · Live compose unlocked"
                                              : sand==="attest" ? "awaiting your checklist" : "not attested yet"}>
                  <p className="sub">There is no on-device sandbox replay route yet. Soft dry-run fire counts
                    stay deleted — this stage is human attestation only. Compose stays locked until you
                    attest every checklist item. A timer never unlocks compose.</p>

                  ${sand==="idle" && html`
                    <button className="btn btn--ai" type="button" onClick=${beginAttest}>
                      <${Ico} d="play" w=${14}/>Open operator checklist · Live</button>`}

                  ${sand!=="idle" && html`
                    <${F}>
                      <div className="sbx">
                        ${CHECKS.map((c,i)=> html`
                          <button key=${c[0]} type="button" className="sbx__r"
                               data-st=${attest[i] || sand==="pass" ? "ok" : "run"}
                               style=${{ "--d":(i*0.08)+"s", textAlign:"left", width:"100%" }}
                               disabled=${sand==="pass"}
                               onClick=${()=>setAttest(a=>({ ...a, [i]: !a[i] }))}>
                            <i/><b>${c[0]}</b><span>${c[1]}</span>
                          </button>`)}
                      </div>
                      ${sand==="attest" && html`
                        <button className="btn btn--go btn--sm" type="button" disabled=${!allAttested}
                          onClick=${confirmAttest}>
                          Attest checklist · unlock Live compose · Live</button>`}
                      ${sand==="pass" && html`
                        <${F}>
                          <${Alert} tone="info" title="Live readiness — not Soft replay"
                            say="Soft fixture fire tables are deleted. Forge merges Live GET /devices/{id}/pins into the pin map before Compose. Re-probe queues a Live ping and re-reads seats."/>
                          <${KV} k="Device" v=${String((device && device.id) || "—")}/>
                          <${KV} k="Soft geometry bound" v=${boundCount + "/" + PIN_ROLES.length}/>
                          <${KV} k="Live ports (API)" v=${String(livePortCount)}
                            tone=${livePortCount ? "go" : "warn"}/>
                          <${KV} k="Live seats (API)"
                            v=${!did ? "no device" : (pinsLive.loading ? "…" : (liveSeatCount + "/" + Math.max(liveSeatItems.length, PIN_ROLES.length)))}
                            tone=${!did || liveSeatCount === 0 ? "warn" : "go"}/>
                          <${KV} k="Live confirmed"
                            v=${!did ? "—" : (pinsLive.loading ? "…" : String(liveConfirmedCount))}
                            tone=${liveConfirmedCount > 0 ? "go" : "warn"}/>
                          ${did && pinsLive.error && html`
                            <${Alert} tone="warn" title="Live pins read failed"
                              say=${String(pinsLive.error)}/>`}
                          ${did && !pinsLive.loading && !pinsLive.error && liveSeatItems.length === 0 && html`
                            <${Alert} tone="warn" title="Zero Live seats"
                              say="Open Estate Pin Board to assign seats, then Re-probe. Soft geometry counts alone never unlock capability truth."/>`}
                          <${KV} k="Board Ask"
                            v=${askLive ? "Live answer on file" : "none this session"}
                            tone=${askLive ? "go" : "warn"}/>
                          <${KV} k="Review model"
                            v=${review.label + (review.purpose ? (" · " + review.purpose) : "")}/>
                          <div className="btn-row fg__acts" style=${{ marginTop:10, flexWrap:"wrap", gap:8 }}>
                            <button className="btn btn--ai btn--sm" type="button"
                              disabled=${!did || probeBusy || pinsLive.loading}
                              onClick=${()=>reProbeLive()}>
                              <${Ico} d="refresh" w=${13}/>${probeBusy ? "Probing · Live…" : "Re-probe · Live"}</button>
                            <button className="btn btn--ghost btn--sm" type="button"
                              onClick=${()=>go("/fleet/device/"+encodeURIComponent(String((device && device.id) || "")))}>
                              Open Board · Live</button>
                          </div>
                          <p className="note">Operator checklist attested. Soft dry.rows stay deleted. Compose uses Live-hydrated seats — a timer never unlocks compose.</p>
                        <//>`}
                    <//>`}
                </${Card}>

                <${Card} title="Compose" meta=${sand==="pass" ? "unlocked · Live forge/compose" : "locked until checklist attestation"}>
                  ${sand!=="pass" && !sent ? html`
                    <div className="fg__gate">
                      <${Ico} d="lock" w=${14}/>
                      <span>Nothing reaches ${device.label} until you attest the checklist above.
                        There is no timer path from a sentence to a relay.</span>
                    </div>` : sent ? html`
                    <div className="fg__done">
                      <${Ico} d="check" w=${16}/>
                      <span><b>Compose receipt.</b> ${composeOut && composeOut.honesty
                        ? composeOut.honesty
                        : "Pin bind/confirm and capability registry writes were attempted on " + device.label + "."}
                        ${(composeOut && composeOut.pin_errors && composeOut.pin_errors.length)
                          ? (" Pin errors: " + composeOut.pin_errors.map(e=>e.seat_id+":"+e.error).join("; "))
                          : ""}</span>
                    </div>` : html`
                    <${F}>
                      <${KV} k="Artifact" v=${rec.id+" · compose draft"}/>
                      <${KV} k="Device" v=${device.id}/>
                      <${KV} k="Write path" v="POST …/forge/compose · Page43 Live"/>
                      <button className="btn btn--ghost btn--sm" type="button" style=${{ marginTop:6 }}
                        disabled=${busy}
                        onClick=${()=> {
                          pinsLive.reload && pinsLive.reload();
                          portsLive.reload && portsLive.reload();
                          toast("Forge seats/ports re-read · Live", "go");
                        }}>
                        Re-read seats · Live</button>
                      ${error && html`<${Alert} tone="bad" title="Compose error · Live" say=${error}/>`}
                      ${isExec && html`
                        <label className="fg__ok">
                          <input type="checkbox" checked=${conf} onChange=${e=>setConf(e.target.checked)}/>
                          <span>I have read what this drives. ${rec.rule.then.filter(t=>/siren|strike|gate/i.test(t)).join("; ")}.</span>
                        </label>`}
                      <button type="button" className="btn btn--go" disabled=${busy || (isExec && !conf)}
                        onClick=${()=>dispatch()}>
                        <${Ico} d="download" w=${14}/>${busy ? "Composing · Live…" : ("Compose onto " + device.label + " · Live")}</button>
                    <//>`}
                </${Card}>
              <//>`}
          <//>`}
      <//>
    </div>`;
}
